import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { fetchItems } from "../itemApi";
import {
  emitCartUpdated,
  formatCategoryLabel,
  getDeliveryLabel,
  getOfferHighlights,
  getProductBadge,
  getResolvedProductType,
  getShippingPreview,
  isRetailVisible,
  readCustomer,
} from "../utils/marketplace";
import { trackAddToCart, trackBeginCheckout, trackFilterUse, trackSearch, trackViewItemList } from "../utils/analytics";
import "./ItemList.css";

const ITEMS_CACHE_KEY = "marketplace-items-cache-v1";

const readCachedItems = () => {
  try {
    const cached = localStorage.getItem(ITEMS_CACHE_KEY);
    const parsed = JSON.parse(cached || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ItemList = () => {
  const [items, setItems] = useState([]);
  const [qtyMap, setQtyMap] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [usingCachedItems, setUsingCachedItems] = useState(false);
  const navigate = useNavigate();
  const lastTrackedCatalogKeyRef = useRef("");

  useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) return undefined;
    const timeout = window.setTimeout(() => {
      trackSearch(trimmed, "storefront_catalog");
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const applyItems = (incomingItems) => {
    const availableItems = Array.isArray(incomingItems) ? incomingItems : [];

    setItems(availableItems);

    const initialQty = {};
    availableItems.forEach((item) => {
      initialQty[item._id] = 1;
    });
    setQtyMap(initialQty);

    return availableItems;
  };

  useEffect(() => {
    const applyLatestCache = () => {
      const cachedItems = readCachedItems();
      if (cachedItems.length) {
        applyItems(cachedItems);
        setUsingCachedItems(true);
      }
    };

    const handleStorage = (event) => {
      if (event.key === ITEMS_CACHE_KEY) {
        applyLatestCache();
      }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("marketplace-items-cache-updated", applyLatestCache);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("marketplace-items-cache-updated", applyLatestCache);
    };
  }, []);

  useEffect(() => {
    let hasCachedItems = false;
    const parsed = readCachedItems();
    if (parsed.length) {
      applyItems(parsed);
      setUsingCachedItems(true);
      hasCachedItems = true;
    }

    (async () => {
      setLoading(true);
      setLoadError("");
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          const res = await fetchItems();
          const data = res.data || res;
          const availableItems = applyItems(data);
          localStorage.setItem(ITEMS_CACHE_KEY, JSON.stringify(availableItems));
          setUsingCachedItems(false);
          setLoading(false);
          return;
        } catch (err) {
          console.error(`Error fetching items (attempt ${attempt}):`, err);
          if (attempt === 2) {
            setLoadError(
              hasCachedItems
                ? "We are showing your last available listings while the live catalog wakes up."
                : "Listings are taking longer than usual to load. Please try again in a few seconds."
            );
          } else {
            await new Promise((resolve) => setTimeout(resolve, 1800));
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const resolvedType = getResolvedProductType(item);
      const visibleForPublicStore = isRetailVisible(item);
      const matchesFilter = activeFilter === "all" || resolvedType === activeFilter;
      const matchesCategory =
        activeCategory === "all" ||
        String(item.category || "").trim().toLowerCase() === activeCategory;
      const query = search.trim().toLowerCase();
      const matchesSearch =
        !query ||
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.sellerName?.toLowerCase().includes(query);
      return visibleForPublicStore && matchesFilter && matchesCategory && matchesSearch;
    });
  }, [activeCategory, activeFilter, items, search]);

  useEffect(() => {
    if (loading || !filteredItems.length) return;
    const trimmedSearch = search.trim().toLowerCase();
    const trackingKey = `${activeFilter}|${activeCategory}|${trimmedSearch}|${filteredItems.map((item) => item._id).join(",")}`;
    if (trackingKey === lastTrackedCatalogKeyRef.current) return;

    trackViewItemList({
      items: filteredItems,
      listName: "Storefront catalog",
      filterType: activeFilter,
      category: activeCategory,
      searchTerm: trimmedSearch,
    });
    lastTrackedCatalogKeyRef.current = trackingKey;
  }, [activeCategory, activeFilter, filteredItems, loading, search]);

  const counts = useMemo(
    () => ({
      all: items.length,
      "live-fish": items.filter((item) => getResolvedProductType(item) === "live-fish").length,
      accessory: items.filter((item) => getResolvedProductType(item) !== "live-fish").length,
    }),
    [items]
  );

  const categoryOptions = useMemo(() => {
    const categories = Array.from(
      new Set(
        items
          .map((item) => String(item.category || "").trim().toLowerCase())
          .filter(Boolean)
      )
    );

    return categories.sort();
  }, [items]);

  // Increase quantity
  const increaseQty = (item) => {
    setQtyMap((prev) => {
      const current = prev[item._id] || 1;

      if (current >= item.stockQty) {
        alert(`Only ${item.stockQty} items available`);
        return prev;
      }

      return { ...prev, [item._id]: current + 1 };
    });
  };

  // Decrease quantity
  const decreaseQty = (item) => {
    setQtyMap((prev) => {
      const current = prev[item._id] || 1;
      return current > 1
        ? { ...prev, [item._id]: current - 1 }
        : prev;
    });
  };

  // ADD TO CART
  const handleAddToCart = (item) => {
    const customer = readCustomer();
    if (!customer?.token) {
      sessionStorage.setItem("redirectAfterLogin", "/");
      navigate("/customer-login");
      return;
    }

    if (item.stockQty === 0) {
      alert("Item is out of stock");
      return;
    }

    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const qty = qtyMap[item._id] || 1;

    const index = cart.findIndex((i) => i._id === item._id);
    const resolvedType = getResolvedProductType(item);

    if (index !== -1) {
      const newQty = cart[index].qty + qty;

      if (newQty > item.stockQty) {
        alert(`Only ${item.stockQty} items available`);
        return;
      }

      cart[index].qty = newQty;
    } else {
      if (qty > item.stockQty) {
        alert(`Only ${item.stockQty} items available`);
        return;
      }

      cart.push({
        _id: item._id,
        name: item.name,
        price: item.price,
        qty: qty,
        image: item.imageUrl || item.image,
        productType: resolvedType,
        category: item.category,
        shippingRates: item.shippingRates,
        sellerName: item.sellerName || item.sellerId?.storeName || "",
        stockQty: item.stockQty,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    emitCartUpdated();
    trackAddToCart(item, qty);
    alert("Added to cart!");
  };

  // BUY NOW
  const handleBuyNow = (item) => {
    const customer = readCustomer();
    if (!customer?.token) {
      sessionStorage.setItem("redirectAfterLogin", "checkout");
      navigate("/customer-login");
      return;
    }

    if (item.stockQty === 0) {
      alert("Item is out of stock");
      return;
    }

    const qty = qtyMap[item._id] || 1;

    if (qty > item.stockQty) {
      alert(`Only ${item.stockQty} items available`);
      return;
    }

    const buyNowItem = {
      _id: item._id,
      name: item.name,
      price: item.price,
      qty: qty,
        image: item.imageUrl || item.image,
      productType: getResolvedProductType(item),
      category: item.category,
      shippingRates: item.shippingRates,
      sellerName: item.sellerName || item.sellerId?.storeName || "",
      stockQty: item.stockQty,
    };

    localStorage.setItem("buy-now-item", JSON.stringify(buyNowItem));
    trackBeginCheckout({
      items: [buyNowItem],
      value: Number(buyNowItem.price || 0) * Number(buyNowItem.qty || 1),
      source: "buy_now",
    });

    navigate("/checkout");
  };

  return (
    <div className="item-list-container">
      <section className="hero-strip">
        <div>
          <div className="eyebrow">Tamil Nadu Live Fish</div>
          <h1 className="item-list-heading">Live fish and aquarium essentials</h1>
          <p className="hero-subtext">
            Fish delivery inside Tamil Nadu. Accessories shipping across India.
          </p>
        </div>
        <div className="hero-meta">
          <span>{counts["live-fish"]} fish</span>
          <span>{counts.accessory} accessories</span>
        </div>
      </section>

      <section className="catalog-toolbar">
        <div className="toolbar-left">
          <div className="filter-pills">
            <button
              type="button"
              className={activeFilter === "all" ? "active" : ""}
              onClick={() => {
                setActiveFilter("all");
                trackFilterUse({ filterType: "product_type", filterValue: "all", context: "storefront_catalog" });
              }}
            >
              All ({counts.all})
            </button>
            <button
              type="button"
              className={activeFilter === "live-fish" ? "active" : ""}
              onClick={() => {
                setActiveFilter("live-fish");
                trackFilterUse({ filterType: "product_type", filterValue: "live-fish", context: "storefront_catalog" });
              }}
            >
              Live Fish ({counts["live-fish"]})
            </button>
            <button
              type="button"
              className={activeFilter === "accessory" ? "active" : ""}
              onClick={() => {
                setActiveFilter("accessory");
                trackFilterUse({ filterType: "product_type", filterValue: "accessory", context: "storefront_catalog" });
              }}
            >
              Accessories ({counts.accessory})
            </button>
          </div>

          <select
            className="category-select"
            value={activeCategory}
            onChange={(event) => {
              setActiveCategory(event.target.value);
              trackFilterUse({
                filterType: "category",
                filterValue: event.target.value,
                context: "storefront_catalog",
              });
            }}
          >
            <option value="all">All Categories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {formatCategoryLabel(category)}
              </option>
            ))}
          </select>
        </div>

        <input
          className="catalog-search"
          placeholder="Search fish, tanks, filters, sellers..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loading ? (
        <div className="catalog-state">
          <strong>Loading listings...</strong>
          <span>
            The store may take a few seconds to wake up after inactivity.
          </span>
        </div>
      ) : null}

      {loadError ? (
        <div className={`catalog-state ${usingCachedItems ? "is-warning" : "is-error"}`}>
          <strong>{usingCachedItems ? "Showing saved listings" : "Unable to load listings"}</strong>
          <span>{loadError}</span>
          <button
            type="button"
            className="catalog-retry"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {!loading && filteredItems.length === 0 ? (
        <p>No items available.</p>
      ) : (
        <div className="item-grid">
          {filteredItems.map((item) => {
            const qty = qtyMap[item._id] || 1;
            const isOutOfStock = item.stockQty === 0;
            const offerHighlights = getOfferHighlights(item);

            return (
              <div key={item._id} className="item-card">
                <div className="item-image">
                  <Link to={`/product/${item._id}`} aria-label={`Open ${item.name}`}>
                    <img src={item.imageUrl || item.image} alt={item.name} />
                  </Link>

                  {item.videoUrl && (
                    <a
                      href={item.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-link"
                    >
                      ▶ Watch Video
                    </a>
                  )}
                </div>

                <div className="item-details">
                  <div className="item-badges">
                    <span className="badge">{getProductBadge(item)}</span>
                    <span className="category-chip">{formatCategoryLabel(item.category)}</span>
                    {item.sellerName || item.sellerId?.storeName ? (
                      <span className="seller-chip">{item.sellerName || item.sellerId?.storeName}</span>
                    ) : null}
                  </div>
                  <h3>
                    <Link to={`/product/${item._id}`} style={{ color: "inherit", textDecoration: "none" }}>
                      {item.name}
                    </Link>
                  </h3>
                  <p>{item.description}</p>
                  <Link
                    to={`/product/${item._id}`}
                    style={{ color: "#0f766e", fontWeight: 800, display: "inline-block", marginBottom: 8 }}
                  >
                    View full details
                  </Link>

                  <div className="item-price">
                    {item.price ? `₹${item.price}` : "Price not available"}
                  </div>
                  <div className="shipping-note">{getDeliveryLabel(item)}</div>
                  <div className="shipping-mini">Shipping from ₹{getShippingPreview(item)}</div>
                  {offerHighlights.length ? (
                    <div className="offer-strip" aria-label="Available offers">
                      {offerHighlights.map((offer) => (
                        <span key={`${offer.badge}-${offer.text}`}>
                          <strong>{offer.badge}</strong>
                          <small>{offer.text}</small>
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <div className={`stock-label ${isOutOfStock ? "is-out" : ""}`}>
                    {isOutOfStock
                      ? "Out of Stock"
                      : `Available: ${item.stockQty}`}
                  </div>

                  {/* QTY SELECTOR */}
                  <div className="qty-selector">
                    <button onClick={() => decreaseQty(item)}>-</button>
                    <div>{qty}</div>
                    <button onClick={() => increaseQty(item)}>+</button>
                  </div>

                  <div className="item-actions">
                    <button
                      onClick={() => handleAddToCart(item)}
                      disabled={isOutOfStock}
                      className="cart-button"
                    >
                      Add to Cart
                    </button>

                    <button
                      onClick={() => handleBuyNow(item)}
                      disabled={isOutOfStock}
                      className="buy-button"
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ItemList;
